// Data Pipeline — the flow diagram (hero). Port of the approved design
// (design-bundle/diagram.js §5). SVG draws geometry only (rails, marching
// flow overlays, arrowheads); ALL text is HTML absolutely positioned as a
// percentage of the viewBox — crisp type at every scale, live numbers tick
// without touching the SVG. Geometry + derivation live in lib/flow-layout.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useScrollAffordance } from './scroll-table';
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import type {
  PipelineFlowSnapshot,
} from '@/api/types';
import {
  DP_CARD, DP_EDGES, DP_NODES, DP_NODE_GROUP, DP_VB,
  dpArrow, dpDerive, dpElbow, dpHealthColor,
} from '@/lib/flow-layout';
import type { DpEdge, DpEdgeId, DpNode, DpNodeId } from '@/lib/flow-layout';
import { traceEdgeActive } from '@/lib/flow-lineage';
import type { TraceOverlay } from '@/lib/flow-lineage';

// ── Ticking number: flashes accent + rises 2px on change (no reflow) ───────

export function DPTick({ children, minCh }: { children: ReactNode; minCh?: number }) {
  const [flash, setFlash] = useState(false);
  const prev = useRef(children);
  useEffect(() => {
    if (prev.current !== children) {
      prev.current = children;
      setFlash(false);
      const id = requestAnimationFrame(() => setFlash(true));
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [children]);
  return (
    <span
      className={'v' + (flash ? ' dp-ticked' : '')}
      style={minCh ? { minWidth: minCh + 'ch' } : undefined}
      onAnimationEnd={() => setFlash(false)}
    >
      {children}
    </span>
  );
}

/** Wall-clock re-render tick for countdowns / relative ages. */
export function useNowMs(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

// ── The diagram ────────────────────────────────────────────────────────────

export type FlowMode = 'live' | 'history';

interface TipState {
  x: number;
  y: number;
  content: ReactNode;
}

export interface FlowDiagramProps {
  snapshot: PipelineFlowSnapshot;
  mode: FlowMode;
  nowMs: number;
  /** History-mode labels: edge ids (e1…) + 'n-<nodeId>' node stat lines. */
  historyTotals?: Record<string, string> | null;
  /**
   * History-mode node tooltips: the RANGE's figures per node. A history
   * tooltip never shows the live snapshot's numbers under a RANGE header.
   */
  historyTips?: Partial<Record<DpNodeId, Record<string, string>>> | null;
  rangeLabel?: string;
  selected: DpNodeId | null;
  onSelect: (id: DpNodeId | null) => void;
  /**
   * Production scope is active. The encore + activation lanes can't be scoped
   * truthfully server-side, so their live stats stay GLOBAL — annotated.
   */
  scoped?: boolean;
  /**
   * TRACE overlay: when set, nodes/edges paint by ONE item's per-stage status
   * (delivered/retry/failed/traversed/pending/unreached) instead of aggregate
   * health. `null` leaves the live/history rendering byte-identical.
   */
  trace?: TraceOverlay | null;
  /** Clicking the selected node deselects it (`onSelect(null)`). Off while a trace is
   *  open, where a click always names its node: it selects that node's newest step. */
  toggle?: boolean;
}

// Encore/activation node stats that stay global under a production scope
// (d1-stats reads the rollup in scope, so no marker).
const GLOBAL_WHEN_SCOPED: ReadonlySet<DpNodeId> = new Set([
  'encore-pg', 'ingest-jobs', 'store-tickets', 'conversion-queue', 'meta', 'ttd',
]);

// In HISTORY the web and ticket lanes are read for the selected production;
// the conversions history is not (the queue keeps no production), so its
// nodes and edges say so.
const GLOBAL_IN_HISTORY_NODES: ReadonlySet<DpNodeId> = new Set(['conversion-queue', 'meta', 'ttd']);
const GLOBAL_IN_HISTORY_EDGES: ReadonlySet<DpEdgeId> = new Set(['e6', 'e11', 'e12', 'e13']);

export function FlowDiagram({
  snapshot, mode, nowMs, historyTotals = null, historyTips = null, rangeLabel = '',
  selected, onSelect, scoped = false, trace = null, toggle = true,
}: FlowDiagramProps) {
  const [tip, setTip] = useState<TipState | null>(null);
  const derived = useMemo(() => dpDerive(snapshot, nowMs), [snapshot, nowMs]);
  const nodeById = useMemo(() => new Map(DP_NODES.map((n) => [n.id, n])), []);
  const traceOn = !!trace;
  const history = mode === 'history';
  const pc = (x: number, y: number): CSSProperties => ({
    left: (x / DP_VB.w) * 100 + '%',
    top: (y / DP_VB.h) * 100 + '%',
  });

  const showTip = (e: ReactMouseEvent, content: ReactNode) => setTip({ x: e.clientX, y: e.clientY, content });
  const moveTip = (e: ReactMouseEvent) => setTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t));
  const hideTip = () => setTip(null);
  // Edge fades for the phone, where the diagram is wider than its card.
  const wrapRef = useRef<HTMLDivElement>(null);
  const edge = useScrollAffordance(wrapRef);

  const nodeTip = (n: DpNode): ReactNode => {
    const d = derived.nodes[n.id];
    const rows = history
      ? (historyTips?.[n.id] ?? { 'range total': historyTotals?.['n-' + n.id] ?? '—' })
      : d.tip;
    return (
      <>
        <div className="dp-tip-hd">
          <span className="d" style={{ background: dpHealthColor(mode === 'history' ? 'neutral' : d.health) }}/>
          {n.name}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--ink-3)', marginLeft: 'auto', letterSpacing: '.08em' }}>
            {history ? 'RANGE' : d.health.toUpperCase()}
          </span>
        </div>
        {Object.entries(rows).map(([k, v]) => (
          <div className="dp-tip-row" key={k}><span className="k">{k}</span><span className="v">{v}</span></div>
        ))}
      </>
    );
  };

  const edgeTip = (e: DpEdge): ReactNode => {
    const st = derived.edges[e.id];
    const lbl = mode === 'history'
      ? (historyTotals?.[e.id] ?? '—') + (scoped && GLOBAL_IN_HISTORY_EDGES.has(e.id) ? ' · global' : '')
      : st.num + st.unit;
    return (
      <>
        <div className="dp-tip-hd">{nodeById.get(e.from)?.name} → {nodeById.get(e.to)?.name}</div>
        <div className="dp-tip-row">
          <span className="k">{mode === 'history' ? 'range total' : 'last tick'}</span>
          <span className="v">{lbl}</span>
        </div>
        {mode !== 'history' && (
          <div className="dp-tip-row">
            <span className="k">flow</span>
            <span className="v">{st.cls === 'stopped' ? 'BLOCKED — downstream down' : st.speed.toUpperCase()}</span>
          </div>
        )}
        <div className="dp-tip-row">
          <span className="k">downstream</span>
          <span className="v">{mode === 'history' ? nodeById.get(e.to)?.name : derived.nodes[e.to].health.toUpperCase()}</span>
        </div>
      </>
    );
  };

  return (
    <div className="dp-diagram-frame scroll-table-frame" data-scroll={edge}>
    <div className="dp-diagram-wrap" ref={wrapRef} role="region" aria-label="Pipeline flow diagram; scroll horizontally to inspect all stages" tabIndex={0} onMouseLeave={hideTip}>
      <div
        className={'dp-diagram' + (mode === 'history' ? ' history' : '') + (traceOn ? ' trace' : '')}
        onClick={(e) => {
          const t = e.target as Element;
          if (t.closest && !t.closest('.dp-node, .dp-pill, .dp-hit')) onSelect(null);
        }}
      >
        <svg viewBox={`0 0 ${DP_VB.w} ${DP_VB.h}`} preserveAspectRatio="none" style={{ pointerEvents: 'none' }}>
          {DP_EDGES.map((e) => {
            const st = derived.edges[e.id];
            const d = dpElbow(e.pts);
            const n = e.pts.length;
            // A trace lights only the edges on the item's own path: both ends
            // reached, so the web and ticket wires into the queue never both light.
            const edgeCls = traceOn
              ? traceEdgeActive(trace!, e.from, e.to)
                ? 'trace-active trace-' + (trace!.nodes[e.to]?.status ?? 'unreached')
                : 'trace-dim'
              : mode === 'history'
                ? 'history'
                : st.cls;
            return (
              <g key={e.id} className={'dp-edge ' + edgeCls}>
                <path className="dp-rail" d={d}/>
                {!st.static && !traceOn && <path className="dp-flow" d={d}/>}
                <polygon className="dp-arrow" points={dpArrow(e.pts[n - 2]!, e.pts[n - 1]!)}/>
                {e.bi && <polygon className="dp-arrow" points={dpArrow(e.pts[1]!, e.pts[0]!)}/>}
                <path
                  className="dp-hit" d={d} style={{ pointerEvents: 'stroke' }}
                  onClick={(ev) => { ev.stopPropagation(); onSelect(e.to); }}
                  onMouseEnter={(ev) => showTip(ev, edgeTip(e))} onMouseMove={moveTip} onMouseLeave={hideTip}
                />
              </g>
            );
          })}
        </svg>

        {/* Lane labels */}
        <div className="dp-lane" style={pc(40, 22)}>Web signals</div>
        <div className="dp-lane" style={pc(40, 276)}>EncorePro</div>
        <div className="dp-lane" style={pc(40, 436)}>Activation</div>

        {/* History banner */}
        {mode === 'history' && (
          <div
            className="dp-lane"
            style={{
              ...pc(40, 508), color: 'var(--ink-2)', border: '1px solid var(--line)',
              background: 'var(--bg-elev)', padding: '4px 10px', borderRadius: 'var(--r)', letterSpacing: '.14em',
            }}
          >
            TOTALS · {rangeLabel}
          </div>
        )}

        {/* Legend (live mode) */}
        {mode !== 'history' && (
          <div className="dp-legend" style={pc(40, 476)}>
            <div className="dp-legend-row">
              <span className="dp-legend-item"><span className="d" style={{ background: 'var(--good)' }}/>UP</span>
              <span className="dp-legend-item"><span className="d" style={{ background: 'var(--warn)' }}/>DEGRADED</span>
              <span className="dp-legend-item"><span className="d" style={{ background: 'var(--bad)' }}/>DOWN</span>
              <span className="dp-legend-item"><span className="d" style={{ background: 'var(--ink-3)' }}/>QUIET</span>
            </div>
            <div className="dp-legend-row">
              <span className="dp-legend-item"><span className="ln"/>MARCH SPEED = THROUGHPUT</span>
              <span className="dp-legend-item"><span className="ln" style={{ borderColor: 'var(--bad)' }}/>STOPPED = BLOCKED</span>
            </div>
          </div>
        )}

        {/* Node cards — HTML over SVG */}
        {DP_NODES.map((n) => {
          const d = derived.nodes[n.id];
          const globalStat = scoped && (history
            ? GLOBAL_IN_HISTORY_NODES.has(n.id)
            : GLOBAL_WHEN_SCOPED.has(n.id) && DP_NODE_GROUP[n.id] !== 'sources');
          const nodeCls = traceOn
            ? 'trace-' + (trace!.nodes[n.id]?.status ?? 'unreached')
            : d.health;
          return (
            <div
              key={n.id}
              role="button"
              tabIndex={0}
              aria-label={`${n.name}: ${d.stat}. Inspect stage`}
              aria-pressed={selected === n.id}
              onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();onSelect(toggle&&selected===n.id?null:n.id);}}}
              className={'dp-node ' + nodeCls + (selected === n.id ? ' selected' : '')}
              style={{
                ...pc(n.x, n.y),
                width: (DP_CARD.w / DP_VB.w) * 100 + '%',
                height: (DP_CARD.h / DP_VB.h) * 100 + '%',
              }}
              onClick={(e) => { e.stopPropagation(); hideTip(); onSelect(toggle && selected === n.id ? null : n.id); }}
              onMouseEnter={(e) => showTip(e, nodeTip(n))} onMouseMove={moveTip} onMouseLeave={hideTip}
            >
              <span className={'dp-node-dot' + (d.fresh && mode !== 'history' && !traceOn ? ' fresh' : '')}/>
              <div className="dp-node-kind" title={n.kind}>{n.kind}</div>
              <div className="dp-node-name">{n.name}</div>
              <div className="dp-node-stat" title={globalStat ? 'Not scoped to the selected production' : undefined}>
                {mode === 'history' ? (historyTotals?.['n-' + n.id] ?? d.stat) : d.stat}
                {globalStat && <span style={{ color: 'var(--ink-3)' }}> · global</span>}
              </div>
            </div>
          );
        })}

        {/* Edge label pills — HTML over SVG, tabular, tick without layout shift */}
        {DP_EDGES.map((e) => {
          const st = derived.edges[e.id];
          const histLabel = historyTotals?.[e.id];
          const globalEdge = scoped && history && GLOBAL_IN_HISTORY_EDGES.has(e.id);
          return (
            <div
              key={e.id}
              className={
                'dp-pill' + (e.align === 'left' ? ' left' : '') + (st.static ? ' static' : '')
                + (st.cls === 'stopped' && mode !== 'history' ? ' stopped' : '')
              }
              style={pc(e.pill[0], e.pill[1])}
              onClick={(ev) => { ev.stopPropagation(); onSelect(e.to); }}
              onMouseEnter={(ev) => showTip(ev, edgeTip(e))} onMouseMove={moveTip} onMouseLeave={hideTip}
            >
              {history
                ? <><b>{histLabel ?? (st.static ? 'NIGHTLY' : '—')}</b>{globalEdge && <span style={{ color: 'var(--ink-3)' }}> · global</span>}</>
                : <>{e.tick ? <DPTick minCh={5}>{st.num}</DPTick> : <b>{st.num}</b>}{st.unit}</>}
            </div>
          );
        })}
      </div>

      {tip && (
        <div className="dp-tip" style={{ left: Math.min(tip.x + 14, window.innerWidth - 250), top: tip.y + 14 }}>
          {tip.content}
        </div>
      )}
    </div>
    <div className="dp-diagram-hint phone-only mono">Swipe to inspect the whole flow</div>
    </div>
  );
}
